/** @jsxImportSource @emotion/react */
import { FunctionComponent, ReactElement, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { match, useParams } from 'react-router-dom';
import { DndProvider, useDrag } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { useHotkeys } from 'react-hotkeys-hook';
import {
    components as commonComponents,
    annotations as commonAnnotations,
    hooks as commonHooks,
} from '@sitebuilder/common';
import { editSvg, grabSvg, iconTrash, moreSvg } from '../assets/images';
import { StateShape as ReduxStateShape } from '../reducers';
import { actionCreators } from '../reducers/templateEditor';
import { actionCreators as compActionCreator } from '../reducers/components';
import { actionCreators as themesActionCreator } from '../reducers/themes';
import { actionCreators as envActionCreators } from '../reducers/envDiff';
import AdminMainPanel from '../components/Layouts/AdminMainPanel';
import TopBar from '../components/Layouts/TopBar';
import LeftSideBar from '../components/Layouts/LeftSidebar';
import {
    INITIAL_LEFT_SIDEBAR_WIDTH,
    MAX_LEFT_SIDEBAR_WIDTH,
} from '../components/Layouts/LeftSidebar/SecondLeftSidebar';
import {
    INITIAL_RIGHT_SIDEBAR_WIDTH,
    MAX_RIGHT_SIDEBAR_WIDTH,
} from '../components/Layouts/RightSideBar';
import { actionCreators as resourcesActionCreators } from '../reducers/resources';
import { EditorPlaceholder } from '../components/Placeholder';
import Toolbar from './Toolbar';
import TemplateView from './TemplateView';
import templateEditorStyles from './TemplateEditor.styles';
import ThemeEditor from './ThemeEditor';

const APP_WIDTH_MARGIN = 150;

interface PropsShape {
    match: match<{
        documentName: string;
        componentId: string;
        libraryName: string;
        themeId: string;
    }>;
}

export const WidgetMenu = ({
    widgetUUID,
    handleRemoveElement,
    setShowRightSidebar,
}: {
    widgetUUID: string;
    handleRemoveElement: () => void;
    setShowRightSidebar: (show: boolean) => void;
}) => {
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const [isCopyComponent, setIsCopyComponent] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    useEffect(() => {
        const handlePositionUpdate = (event: CustomEvent) => {
            const { top, left } = event.detail;
            setPosition({ top, left });
        };

        window.addEventListener('widget-menu-position', handlePositionUpdate as EventListener);

        return () => {
            window.removeEventListener(
                'widget-menu-position',
                handlePositionUpdate as EventListener
            );
        };
    }, []);

    useEffect(() => {
        if (isMenuOpen) {
            setIsMenuOpen(false);
        }

        const el = document.getElementById(`placeholder-${widgetUUID}`);
        const appCanvas = document.getElementById('app-canvas');

        if (el && appCanvas) {
            const rect = el.getBoundingClientRect();
            const appCanvasRect = appCanvas.getBoundingClientRect();

            setPosition({
                top: rect.top - appCanvasRect.top - 20,
                left: rect.left - appCanvasRect.left + rect.width - 40,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [widgetUUID]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (
                !target.closest('.settings-bar') &&
                !target.closest('.settings-dropdown') &&
                isMenuOpen
            ) {
                setIsMenuOpen(false);
            }
        };

        document.addEventListener('click', handleClickOutside);

        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [isMenuOpen]);

    useHotkeys('ctrl+c', () => setIsCopyComponent(true), { keydown: true, keyup: false });
    useHotkeys('ctrl+c', () => setIsCopyComponent(false), { keyup: true, keydown: false });

    const [{ isDragging }, drag] = useDrag(
        () => ({
            type: 'WIDGET',
            item: () => {
                return {
                    id: '',
                    widgetUUID,
                    dragMode: isCopyComponent ? 'copy' : 'move',
                    isRefComponent: false,
                    isPublicRefComponent: false,
                };
            },
            collect: (monitor) => ({
                isDragging: monitor.isDragging(),
            }),
        }),
        [widgetUUID, isCopyComponent]
    );

    return (
        <div
            className="settings-bar"
            style={{
                top: `${position.top}px`,
                left: `${position.left}px`,
                display: isDragging ? 'none' : 'flex',
            }}
        >
            <div className="drag-icon" ref={drag}>
                <img src={grabSvg} alt="Drag icon" />
            </div>

            <div className="menu-toggle" onClick={() => setIsMenuOpen((prev) => !prev)}>
                <img src={moreSvg} alt="More options" />
            </div>

            {isMenuOpen && (
                <div className="settings-dropdown">
                    <div
                        className="menu-item"
                        onClick={() => {
                            setIsMenuOpen(false);
                            setShowRightSidebar(true);
                        }}
                    >
                        <span>Edit</span>
                        <img className="menu-icon" src={editSvg} alt="icom-save-svg" />
                    </div>
                    <div className="menu-item" onClick={handleRemoveElement}>
                        <span>Remove</span>
                        <img className="menu-icon" src={iconTrash} alt="Remove icon" />
                    </div>
                </div>
            )}
        </div>
    );
};

const TemplateEditor: FunctionComponent<React.PropsWithChildren<PropsShape>> = (
    props: PropsShape
): ReactElement => {
    const appContext = commonHooks.useAppContext();
    const appCanvasRef = useRef<HTMLDivElement>(null);

    const [sidebarWidths, setSidebarWidths] = useState({
        left: INITIAL_LEFT_SIDEBAR_WIDTH,
        right: INITIAL_RIGHT_SIDEBAR_WIDTH,
    });
    const [zoomPercentage, setZoomPercentage] = useState<number>(100);
    const [showRightSidebar, setShowRightSidebar] = useState<boolean>(false);

    const params: any = useParams();
    const { locale } = params;

    const {
        data,
        document: doc,
        documentType,
        colorPalettes,
        documentMetadata,
        accessToken,
        theme: selectedTheme,
        originalHostname,
        baseUrl,
        url,
        isDocumentLoaded,
        isComponentLoaded,
        editor,
        siteResourceStrings,
        selectedId,
        widgetInstances,
    } = useSelector((state: ReduxStateShape) => {
        return {
            selectedId: state.templateEditor.selectedId,
            data: state.templateEditor.data,
            document: state.templateEditor.document,
            documentType: state.app.documentType,
            documentMetadata: state.templateEditor.documentMetadata,
            accessToken: state.auth.user?.access_token,
            theme: state.themes.theme,
            colorPalettes: state.themes.colorPalettes,
            originalHostname: state.app.originalHostname,
            baseUrl: state.app.baseUrl,
            url: state.app.url,
            isDocumentLoaded: state.templateEditor.isDocumentLoaded,
            isComponentLoaded: state.components.isComponentLoaded,
            editor: state.templateEditor.editor,
            siteResourceStrings: state.resources?.resources?.[locale || 'en-US'],
            widgetInstances: state.templateEditor.document?.widgetInstances || {},
        };
    });

    const dispatch = useDispatch();
    const { match } = props;
    const { documentName, componentId, libraryName, themeId } = match.params;

    // Get page properties from currently selected widget (if it's a page) or root widget instance
    const selectedWidget = selectedId ? widgetInstances?.[selectedId] : undefined;
    const selectedWidgetIsPage = selectedWidget?.widgetId === 'Page';
    
    // Use selected page widget properties if a page is selected, otherwise fall back to root
    const pageWidget = selectedWidgetIsPage ? selectedWidget : widgetInstances?.root;
    const pageOrientationValue = pageWidget?.properties?.pageOrientation;
    const pageOrientation = typeof pageOrientationValue === 'string' ? pageOrientationValue : 'portrait';
    const pageSizeValue = pageWidget?.properties?.pageSize;
    const pageSize = typeof pageSizeValue === 'string' ? pageSizeValue : 'letter';

    // Get page dimensions based on size (in pixels for editor display)
    const getPageDimensions = (size: string, orientation: string) => {
        const dimensions = {
            letter: { width: 816, height: 1056 }, // 8.5" x 11" at 96dpi
            A4: { width: 794, height: 1123 },     // 210mm x 297mm at 96dpi
            legal: { width: 816, height: 1344 },  // 8.5" x 14" at 96dpi
        };

        const baseDims = dimensions[size as keyof typeof dimensions] || dimensions.letter;
        if (orientation === 'landscape') {
            return { width: baseDims.height, height: baseDims.width };
        }
        return baseDims;
    };

    // Calculate maximum width needed for document container based on root and all pages
    const getMaxDocumentWidth = () => {
        let maxWidth = 816; // default letter width
        
        // First check root document dimensions
        const rootPageDims = getPageDimensions(pageSize, pageOrientation);
        maxWidth = Math.max(maxWidth, rootPageDims.width);
        
        // Then check all widget instances for Page components
        Object.values(widgetInstances).forEach(widget => {
            if (widget?.widgetId === 'Page') {
                const widgetPageSize = typeof widget.properties?.pageSize === 'string' ? widget.properties.pageSize : 'letter';
                const widgetPageOrientation = typeof widget.properties?.pageOrientation === 'string' ? widget.properties.pageOrientation : 'portrait';
                const pageDims = getPageDimensions(widgetPageSize, widgetPageOrientation);
                if (pageDims.width > maxWidth) {
                    maxWidth = pageDims.width;
                }
            }
        });
        
        return maxWidth;
    };

    const pageDimensions = getPageDimensions(pageSize, pageOrientation);
    const documentWidth = getMaxDocumentWidth();
    useEffect(() => {
        if (documentName) {
            dispatch(actionCreators.setEditor('pageEditor'));
            dispatch(actionCreators.loadDocument(documentName));
        }

        if (componentId && libraryName) {
            dispatch(actionCreators.setEditor('componentEditor'));
            dispatch(compActionCreator.loadComponent(libraryName, componentId));
        }

        if (themeId) {
            dispatch(actionCreators.setEditor('themeEditor'));
            dispatch(themesActionCreator.loadTheme(themeId));
        }

        dispatch(envActionCreators.listEnvironments());
        dispatch(resourcesActionCreators.getResources());

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documentName, componentId, libraryName]);

    useEffect(() => {
        const amountToScrollRight = (sidebarWidths.left + APP_WIDTH_MARGIN) / 2;
        const editContainer = document.querySelector('.app');

        if (
            editContainer &&
            sidebarWidths.left === INITIAL_LEFT_SIDEBAR_WIDTH &&
            sidebarWidths.right === 0
        ) {
            editContainer.scrollTo({ left: amountToScrollRight });
        }
    }, [sidebarWidths.left, sidebarWidths.right]);

    // Listen for widget property updates from RichTextEditable widget
    useEffect(() => {
        const handleWidgetPropertyUpdate = (event: CustomEvent) => {
            const { widgetInstanceId, properties } = event.detail;
            console.log('Received widget property update:', { widgetInstanceId, properties });
            
            if (widgetInstanceId && properties && editor) {
                dispatch(actionCreators.saveWidgetProperties(
                    widgetInstanceId,
                    properties,
                    editor
                ));
            }
        };

        window.addEventListener('widgetPropertyUpdate', handleWidgetPropertyUpdate as EventListener);
        
        return () => {
            window.removeEventListener('widgetPropertyUpdate', handleWidgetPropertyUpdate as EventListener);
        };
    }, [dispatch, editor]);
    useEffect(() => {
        if (showRightSidebar) {
            setShowRightSidebar(false);
        }

        const handleScroll = () => {
            if (!selectedId || widgetInstance?.id === 'root') return;

            const el = document.getElementById(`placeholder-${selectedId}`);
            const appCanvas = appCanvasRef.current;

            if (el && appCanvas) {
                const elRect = el.getBoundingClientRect();
                const appCanvasRect = appCanvas.getBoundingClientRect();

                const top = elRect.top - appCanvasRect.top - 20;
                const left = elRect.left - appCanvasRect.left + elRect.width - 40;

                console.log('Widget menu position:', { top, left });

                const event = new CustomEvent('widget-menu-position', {
                    detail: { top, left },
                });

                window.dispatchEvent(event);
            }
        };

        window.addEventListener('scroll', handleScroll, true);

        return () => {
            window.removeEventListener('scroll', handleScroll, true);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedId]);

    const handleRemoveElement = () => {
        dispatch(actionCreators.removeSelected());
    };

    const maxSidebarWidth = Math.max(MAX_RIGHT_SIDEBAR_WIDTH, MAX_LEFT_SIDEBAR_WIDTH);
    const appWidth = `calc(100% - 40px + ${maxSidebarWidth}px)`;
    const widgetInstance = selectedId ? widgetInstances[selectedId] : null;

    return (
        <commonComponents.AppContext.AppContextProvider
            {...{
                ...appContext,
                isClient: true,
                data: {
                    ...data,
                    resourceStrings: { ...siteResourceStrings, ...data?.resourceStrings },
                },
                pageName: documentName,
                componentName: componentId,
                document: doc,
                documentType,
                colorPalettes,
                documentMetadata,
                inEditMode: true,
                accessToken,
                theme: selectedTheme,
                themeSelected: '',
                fundList: { selectedList: [] },
                queries: {},
                originalHostname,
                baseUrl,
                url,
                annotations: commonComponents.AppContext.defaultAnnotations,
                annotator: new commonAnnotations.annotator.Annotator(
                    commonComponents.AppContext.defaultAnnotations
                ),
                setters: {
                    setSelectedList: () => {},
                    setAnnotations: () => {},
                    setQueries: () => {},
                },
                Placeholder: EditorPlaceholder,
            }}
        >
            <DndProvider backend={HTML5Backend}>
                <AdminMainPanel>
                    <Container fluid className={'nav-bar'}>
                        <Row>
                            <Col xs={12}>
                                <TopBar />
                            </Col>
                            <Col xs={2}>
                                {editor && ['pageEditor', 'componentEditor'].includes(editor) && (
                                    <LeftSideBar
                                        setLeftSidebarWidth={(width: number) =>
                                            setSidebarWidths({
                                                left: width,
                                                right: sidebarWidths.right,
                                            })
                                        }
                                    />
                                )}
                            </Col>
                            {(
                                editor === 'componentEditor' ? isComponentLoaded : isDocumentLoaded
                            ) ? (
                                <div
                                    id="app-canvas"
                                    className="app"
                                    css={templateEditorStyles}
                                    ref={appCanvasRef}
                                >
                                    {selectedId && widgetInstance?.id !== 'root' && (
                                        <WidgetMenu
                                            widgetUUID={selectedId}
                                            handleRemoveElement={handleRemoveElement}
                                            setShowRightSidebar={setShowRightSidebar}
                                        />
                                    )}
                                    <div className="pane-header">
                                        <Toolbar
                                            documentName={documentName}
                                            componentId={componentId}
                                            libraryName={libraryName}
                                            sidebarWidths={sidebarWidths}
                                            setRightSidebarWidth={(width: number) =>
                                                setSidebarWidths({
                                                    left: sidebarWidths.left,
                                                    right: width,
                                                })
                                            }
                                            setZoomPercentage={(per) => setZoomPercentage(per)}
                                            widgetFactory={appContext.widgetFactory}
                                            showRightSidebar={showRightSidebar}
                                        />
                                    </div>
                                    <main
                                        className="main"
                                        style={{
                                            width: appWidth,
                                            paddingLeft: pageOrientation === 'landscape' ? '15%' : '25%',
                                            paddingRight: pageOrientation === 'landscape' ? '15%' : '25%',
                                        }}
                                    >
                                        <TemplateView
                                            zoomPercentage={zoomPercentage}
                                            pageOrientation={pageOrientation}
                                            pageDimensions={pageDimensions}
                                            documentWidth={documentWidth}
                                        />
                                    </main>
                                </div>
                            ) : editor === 'themeEditor' ? (
                                <ThemeEditor themeId={themeId} />
                            ) : (
                                <div className={'template-editor-loading'}>Loading...</div>
                            )}
                        </Row>
                    </Container>
                </AdminMainPanel>
            </DndProvider>
        </commonComponents.AppContext.AppContextProvider>
    );
};

export default TemplateEditor;
